import momentOriginal from 'moment';
import moment, { MomentInput } from 'moment-timezone';
import React from 'react';
import ReactMoment, { MomentProps } from 'react-moment';

import { useUserDateTimePreferences } from '@sbu/hooks.time';

import { parseUtcTimestamp } from 'shared/utils/date';

loadLocale();

ReactMoment.globalMoment = moment;

/**
 * Overrides ReactMoment to handle Sumo Logic CSE date formatting and preference
 */
export const Moment = ({
  ago,
  date,
  fromNow,
  format: dateTimeFormatOverride,
  ...rest
}: MomentProps) => {
  const { dateTimeFormat, timezone, isLocal } = useUserDateTimePreferences();
  const isRelative = ago || fromNow;

  return (
    <ReactMoment
      {...rest}
      ago={ago}
      date={date && parseUtcTimestamp(date as MomentInput).toDate()}
      format={
        !isRelative ? dateTimeFormatOverride || dateTimeFormat : undefined
      }
      fromNow={fromNow}
      local={isLocal}
      locale={isRelative ? 'en' : undefined}
      tz={isLocal ? undefined : timezone}
    />
  );
};

function loadLocale() {
  if (navigator.language) {
    // If the user has defined a language preference in their browser, it will
    // be here The language preference will be of the format 'en-US', 'en-GB',
    // 'fr-FR', etc Moment locales must be completely lowercase, but the browser
    // uppercases the country portion Moment supports some lang-country locales,
    // like 'en-gb', but supports some locales only by language, like 'fr'
    const fullLocale = navigator.language.toLowerCase();

    if (fullLocale === 'en-us' || fullLocale === 'en') {
      // default en locale is already bundled with moment, don't attempt to load
      return;
    }

    import(`moment/locale/${fullLocale}`).then(
      () => {
        // There's some sort of issue with setting the locale on the
        // moment-timezone Looks like moment-timezone doesn't have any locale
        // data, so you have to define it
        // https://github.com/moment/moment-timezone/issues/647#issuecomment-439600573
        momentOriginal.locale(fullLocale);
        // "_config" isn't present in the type definition, probably
        // because it's supposed to be private
        // @ts-ignore
        moment.defineLocale(fullLocale, momentOriginal.localeData()._config);
        moment.locale(fullLocale);
      },
      () => {
        const languageOnly = fullLocale.substring(0, 2);
        import(`moment/locale/${languageOnly}`).then(() => {
          momentOriginal.locale(languageOnly);
          moment.defineLocale(
            languageOnly,
            // @ts-ignore
            momentOriginal.localeData()._config
          );
          moment.locale(languageOnly);
        });
      }
    );
  }
}
